import CalendarIcon from '@/components/icons/calendar';
import StyledButton from '@/components/common/styledButton';
import StyledSelect from '@/components/common/styledSelect';
import CustomDataTable from '@/components/table/customDataTable';
import { DataRangeFilterEnum } from '@/constants/dates';
import { INote } from '@/interfaces/notes';
import { getNotes } from '@/services/notes';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import FilterList from '@mui/icons-material/FilterList';
import { Chip, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { GridColDef, type GridRowParams } from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import { useEffect, useState, useContext } from 'react';
import NoteDetail from '../../noteDetail';
import { UserContext } from '@/contexts/userContext';

import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';

const NotesGrid = ({ noteId }: { noteId: number }) => {
  const { currentRole } = useContext(UserContext);

  const [notes, setNotes] = useState<INote[]>([]);

  const [selectedNote, setSelectedNote] = useState<INote | null>(null);

  const [groupBy, setGroupBy] = useState<DataRangeFilterEnum>(
    DataRangeFilterEnum.OpenYear,
  );

  const handleGroupByChange = (event: SelectChangeEvent) => {
    setGroupBy(event.target.value as DataRangeFilterEnum);
  };

  useEffect(() => {
    void getNotes(noteId.toString()).then((data) => {
      setNotes(Array.isArray(data) ? data : [data]);
    });
  }, [noteId]);

  const columns: GridColDef<INote>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 160,
      renderCell: ({ row }) => (
        <Chip
          size='small'
          label={row.type}
          sx={{ fontSize: 12, fontWeight: 600 }}
          color='success'
        ></Chip>
      ),
    },
    ...(currentRole === 'Advisor'
      ? [
          {
            field: 'client',
            headerName: 'Client',
            flex: 1,
          } satisfies GridColDef<INote>,
        ]
      : []),
    {
      field: 'account',
      headerName: 'Account',
      flex: 1,
    },
    {
      field: 'action_needed',
      headerName: 'Action Needed',
      flex: 1,
      renderCell: ({ row }) => <ActionCell action={row.action_needed} />,
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      flex: 1,
      valueGetter: (_value, row) =>
        row.due_date ? dayjs(row.due_date).format('M/D/YY') : '-',
    },
    {
      field: 'creator',
      headerName: 'Creator',
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          direction='row'
          alignItems='center'
          height='100%'
          gap={0.5}
        >
          <Typography
            variant='subtitle2'
            fontWeight={600}
          >
            {row.creator}
          </Typography>
          <Typography fontSize={14}>
            {' '}
            on {dayjs().subtract(15, 'day').format('M/D/YY')}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      flex: 1,
      valueGetter: () => dayjs().subtract(1, 'day').format('M/D/YY'),
      width: GRID_COLUMN_WIDTH_NARROW,
    },
  ];

  const ActionCell = ({ action }: { action: string }) => {
    return (
      <Stack
        direction='row'
        gap={1}
        height='100%'
        alignItems='center'
      >
        {action === 'Completed' && (
          <CheckCircleRounded
            color='success'
            sx={{ width: 16, height: 16 }}
          />
        )}
        {action === 'Follow up' && (
          <ErrorRounded
            color='error'
            sx={{ width: 16, height: 16 }}
          />
        )}

        <Typography
          variant='subtitle2'
          fontWeight={600}
        >
          {action}
        </Typography>
      </Stack>
    );
  };

  return (
    <Stack gap={2}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        gap={2}
      >
        <StyledButton
          variant='outlined'
          startIcon={<FilterList />}
          sx={{
            height: '42px',
          }}
        >
          Filter Notes
        </StyledButton>

        <StyledSelect
          formControlSx={{ width: 220, height: 48 }}
          startAdornment={<CalendarIcon />}
          value={groupBy}
          options={[
            {
              value: DataRangeFilterEnum.OpenYear,
              label: DataRangeFilterEnum.OpenYear,
            },
            {
              value: DataRangeFilterEnum.ClosedQuarter,
              label: DataRangeFilterEnum.ClosedQuarter,
            },
            {
              value: DataRangeFilterEnum.ClosedYear,
              label: DataRangeFilterEnum.ClosedYear,
            },
          ]}
          onChange={handleGroupByChange}
        />
      </Stack>
      <CustomDataTable
        columns={columns}
        rows={notes}
        onRowClick={
          currentRole === 'Advisor'
            ? (params: GridRowParams<INote>) => {
                setSelectedNote(params.row);
              }
            : undefined
        }
      />

      {selectedNote && (
        <NoteDetail
          open={!!selectedNote}
          toggleDrawer={() => {
            setSelectedNote(null);
          }}
        />
      )}
    </Stack>
  );
};

export default NotesGrid;
